// src/config/configRegistry.js
// Static registry for all configs with deep merge support

import { articlesConfig as enConfig } from './articlesConfig.js';
import { hubsConfig as enHubsConfig } from './hubsConfig.js';
import { privacyConfig as enPrivacyConfig } from './privacyConfig.js';
import { termsConfig as enTermsConfig } from './termsConfig.js';
import { cookiesConfig as enCookiesConfig } from './cookiesConfig.js';
import { sanitizeConfig } from '../utils/translationSanitizer';
import { mergeArticleConfig, mergeHubConfig, validateOverride } from '../utils/configMerger';

// We'll import the localized configs here
// For now, we'll use a simple approach
let configs = {
  en: enConfig,
  es: null,
  fr: null,
  de: null,
  pt: null
};

let hubsConfigs = {
  en: enHubsConfig,
  es: null,
  fr: null,
  de: null,
  pt: null
};

let privacyConfigs = {
  en: enPrivacyConfig,
  es: null,
  fr: null,
  de: null,
  pt: null

};

let termsConfigs = {
  en: enTermsConfig,
  es: null,
  fr: null,
  de: null,
  pt: null

};

let cookiesConfigs = {
  en: enCookiesConfig,
  es: null,
  fr: null,
  de: null,
  pt: null

};

// Try to load localized configs with deep merge support
async function loadLocalizedConfigs() {
  // Load articles configs
  try {
    const esModule = await import('./articlesConfig.es.js');
    if (validateOverride(esModule.articlesConfig)) {
      // It's a partial override - merge with base, then sanitize for locale
      const merged = mergeArticleConfig(enConfig, esModule.articlesConfig);
      configs.es = sanitizeConfig(merged, 'es');
      // console.log('✅ Spanish articles config loaded (merged + sanitized)');
    } else {
      // It's a full config - sanitize as-is
      configs.es = sanitizeConfig(esModule.articlesConfig, 'es');
      // console.log('✅ Spanish articles config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ Spanish articles config not available');
  }

  try {
    const frModule = await import('./articlesConfig.fr.js');
    if (validateOverride(frModule.articlesConfig)) {
      // It's a partial override - merge with base, then sanitize for locale
      const merged = mergeArticleConfig(enConfig, frModule.articlesConfig);
      configs.fr = sanitizeConfig(merged, 'fr');
      // console.log('✅ French articles config loaded (merged + sanitized)');
    } else {
      // It's a full config - sanitize as-is
      configs.fr = sanitizeConfig(frModule.articlesConfig, 'fr');
      // console.log('✅ French articles config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ French articles config not available');
  }

  try {
    const deModule = await import('./articlesConfig.de.js');
    if (validateOverride(deModule.articlesConfig)) {
      // It's a partial override - merge with base, then sanitize for locale
      const merged = mergeArticleConfig(enConfig, deModule.articlesConfig);
      configs.de = sanitizeConfig(merged, 'de');
      // console.log('✅ German articles config loaded (merged + sanitized)');
    } else {
      // It's a full config - sanitize as-is
      configs.de = sanitizeConfig(deModule.articlesConfig, 'de');
      // console.log('✅ German articles config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ German articles config not available');
  }

  
  try {
    const ptModule = await import('./articlesConfig.pt.js');
    if (validateOverride(ptModule.articlesConfig)) {
      const merged = mergeArticleConfig(enConfig, ptModule.articlesConfig);
      configs.pt = sanitizeConfig(merged, 'pt');
    } else {
      configs.pt = sanitizeConfig(ptModule.articlesConfig, 'pt');
    }
  } catch (e) {
    // console.log('⚠️ PT articles config not available');
  }try {
    const ptModule = await import('./articlesConfig.pt.js');
    if (validateOverride(ptModule.articlesConfig)) {
      // It's a partial override - merge with base, then sanitize for locale
      const merged = mergeArticleConfig(enConfig, ptModule.articlesConfig);
      configs.pt = sanitizeConfig(merged, 'pt');
      // console.log('✅ Portuguese articles config loaded (merged + sanitized)');
    } else {
      // It's a full config - sanitize as-is
      configs.pt = sanitizeConfig(ptModule.articlesConfig, 'pt');
      // console.log('✅ Portuguese articles config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ Portuguese articles config not available');
  }

  // Load hubs configs
  try {
    const esHubsModule = await import('./hubsConfig.es.js');
    if (validateOverride(esHubsModule.hubsConfig)) {
      // It's a partial override - merge with base
      hubsConfigs.es = mergeHubConfig(enHubsConfig, esHubsModule.hubsConfig);
      // console.log('✅ Spanish hubs config loaded (merged)');
    } else {
      // It's a full config - use as-is
      hubsConfigs.es = sanitizeConfig(esHubsModule.hubsConfig, 'es');
      // console.log('✅ Spanish hubs config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ Spanish hubs config not available');
  }

  try {
    const frHubsModule = await import('./hubsConfig.fr.js');
    if (validateOverride(frHubsModule.hubsConfig)) {
      // It's a partial override - merge with base
      hubsConfigs.fr = mergeHubConfig(enHubsConfig, frHubsModule.hubsConfig);
      // console.log('✅ French hubs config loaded (merged)');
    } else {
      // It's a full config - use as-is
      hubsConfigs.fr = sanitizeConfig(frHubsModule.hubsConfig, 'fr');
      // console.log('✅ French hubs config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ French hubs config not available');
  }

  try {
    const deHubsModule = await import('./hubsConfig.de.js');
    if (validateOverride(deHubsModule.hubsConfig)) {
      // It's a partial override - merge with base
      hubsConfigs.de = mergeHubConfig(enHubsConfig, deHubsModule.hubsConfig);
      // console.log('✅ German hubs config loaded (merged)');
    } else {
      // It's a full config - use as-is
      hubsConfigs.de = sanitizeConfig(deHubsModule.hubsConfig, 'de');
      // console.log('✅ German hubs config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ German hubs config not available');
  }

  try {
    const ptHubsModule = await import('./hubsConfig.pt.js');
    if (validateOverride(ptHubsModule.hubsConfig)) {
      // It's a partial override - merge with base
      hubsConfigs.pt = mergeHubConfig(enHubsConfig, ptHubsModule.hubsConfig);
      // console.log('✅ Portuguese hubs config loaded (merged)');
    } else {
      // It's a full config - use as-is
      hubsConfigs.pt = sanitizeConfig(ptHubsModule.hubsConfig, 'pt');
      // console.log('✅ Portuguese hubs config loaded (full)');
    }
  } catch (e) {
    // console.log('⚠️ Portuguese hubs config not available');
  }

  // Load privacy configs
  try {
    const esPrivacyModule = await import('./privacyConfig.es.js');
    privacyConfigs.es = esPrivacyModule.privacyConfig;
    // console.log('✅ Spanish privacy config loaded');
  } catch (e) {
    // console.log('⚠️ Spanish privacy config not available');
  }

  try {
    const frPrivacyModule = await import('./privacyConfig.fr.js');
    privacyConfigs.fr = frPrivacyModule.privacyConfig;
    // console.log('✅ French privacy config loaded');
  } catch (e) {
    // console.log('⚠️ French privacy config not available');
  }

  try {
    const dePrivacyModule = await import('./privacyConfig.de.js');
    privacyConfigs.de = dePrivacyModule.privacyConfig;
    // console.log('✅ German privacy config loaded');
  } catch (e) {
    // console.log('⚠️ German privacy config not available');
  }

  try {
    const ptPrivacyModule = await import('./privacyConfig.pt.js');
    privacyConfigs.pt = ptPrivacyModule.privacyConfig;
    // console.log('✅ Portuguese privacy config loaded');
  } catch (e) {
    // console.log('⚠️ Portuguese privacy config not available');
  }

  // Load terms configs
  try {
    const esTermsModule = await import('./termsConfig.es.js');
    termsConfigs.es = esTermsModule.termsConfig;
    // console.log('✅ Spanish terms config loaded');
  } catch (e) {
    // console.log('⚠️ Spanish terms config not available');
  }

  try {
    const frTermsModule = await import('./termsConfig.fr.js');
    termsConfigs.fr = frTermsModule.termsConfig;
    // console.log('✅ French terms config loaded');
  } catch (e) {
    // console.log('⚠️ French terms config not available');
  }

  try {
    const deTermsModule = await import('./termsConfig.de.js');
    termsConfigs.de = deTermsModule.termsConfig;
    // console.log('✅ German terms config loaded');
  } catch (e) {
    // console.log('⚠️ German terms config not available');
  }

  try {
    const ptTermsModule = await import('./termsConfig.pt.js');
    termsConfigs.pt = ptTermsModule.termsConfig;
    // console.log('✅ Portuguese terms config loaded');
  } catch (e) {
    // console.log('⚠️ Portuguese terms config not available');
  }

  // Load cookies configs
  try {
    const esCookiesModule = await import('./cookiesConfig.es.js');
    cookiesConfigs.es = esCookiesModule.cookiesConfig;
    // console.log('✅ Spanish cookies config loaded');
  } catch (e) {
    // console.log('⚠️ Spanish cookies config not available');
  }

  try {
    const frCookiesModule = await import('./cookiesConfig.fr.js');
    cookiesConfigs.fr = frCookiesModule.cookiesConfig;
    // console.log('✅ French cookies config loaded');
  } catch (e) {
    // console.log('⚠️ French cookies config not available');
  }

  try {
    const deCookiesModule = await import('./cookiesConfig.de.js');
    cookiesConfigs.de = deCookiesModule.cookiesConfig;
    // console.log('✅ German cookies config loaded');
  } catch (e) {
    // console.log('⚠️ German cookies config not available');
  }

  try {
    const ptCookiesModule = await import('./cookiesConfig.pt.js');
    cookiesConfigs.pt = ptCookiesModule.cookiesConfig;
    // console.log('✅ Portuguese cookies config loaded');
  } catch (e) {
    // console.log('⚠️ Portuguese cookies config not available');
  }
}

export async function loadArticlesConfigForLocale(locale) {
  const lc = (locale || 'en').split('-')[0];
  
  // console.log(`🌍 Loading config for locale: ${lc}`);
  
  // Load localized configs if not already loaded
  if (configs.es === null || configs.fr === null || configs.de === null) {
    await loadLocalizedConfigs();
  }
  
  // console.log(`🔍 Available configs:`, Object.keys(configs).filter(key => configs[key] !== null));
  // console.log(`🔍 Requested locale: ${lc}`);
  // console.log(`🔍 Config for ${lc}:`, configs[lc] ? 'Available' : 'Not available');
  
  if (configs[lc]) {
    // console.log(`✅ Loaded ${lc} config`);
    return configs[lc];
  }
  
  // console.log('✅ Loaded English config (fallback)');
  return configs.en;
}

export async function loadHubsConfigForLocale(locale) {
  const lc = (locale || 'en').split('-')[0];
  
  // console.log(`🌍 Loading hubs config for locale: ${lc}`);
  
  // Load localized configs if not already loaded
  if (hubsConfigs.es === null || hubsConfigs.fr === null || hubsConfigs.de === null) {
    await loadLocalizedConfigs();
  }
  
  // console.log(`🔍 Available hubs configs:`, Object.keys(hubsConfigs).filter(key => hubsConfigs[key] !== null));
  // console.log(`🔍 Requested locale: ${lc}`);
  // console.log(`🔍 Hubs config for ${lc}:`, hubsConfigs[lc] ? 'Available' : 'Not available');
  
  if (hubsConfigs[lc]) {
    // console.log(`✅ Loaded ${lc} hubs config`);
    return hubsConfigs[lc];
  }
  
  // console.log('✅ Loaded English hubs config (fallback)');
  return hubsConfigs.en;
}

export async function loadPrivacyConfigForLocale(locale) {
  const lc = (locale || 'en').split('-')[0];
  
  // console.log(`🌍 Loading privacy config for locale: ${lc}`);
  
  // Load localized configs if not already loaded
  if (privacyConfigs.es === null || privacyConfigs.fr === null || privacyConfigs.de === null) {
    await loadLocalizedConfigs();
  }
  
  if (privacyConfigs[lc]) {
    // console.log(`✅ Loaded ${lc} privacy config`);
    return privacyConfigs[lc];
  }
  
  // console.log('✅ Loaded English privacy config (fallback)');
  return privacyConfigs.en;
}

export async function loadTermsConfigForLocale(locale) {
  const lc = (locale || 'en').split('-')[0];
  
  // console.log(`🌍 Loading terms config for locale: ${lc}`);
  
  // Load localized configs if not already loaded
  if (termsConfigs.es === null || termsConfigs.fr === null || termsConfigs.de === null) {
    await loadLocalizedConfigs();
  }
  
  if (termsConfigs[lc]) {
    // console.log(`✅ Loaded ${lc} terms config`);
    return termsConfigs[lc];
  }
  
  // console.log('✅ Loaded English terms config (fallback)');
  return termsConfigs.en;
}

export async function loadCookiesConfigForLocale(locale) {
  const lc = (locale || 'en').split('-')[0];
  
  // console.log(`🌍 Loading cookies config for locale: ${lc}`);
  
  // Load localized configs if not already loaded
  if (cookiesConfigs.es === null || cookiesConfigs.fr === null || cookiesConfigs.de === null) {
    await loadLocalizedConfigs();
  }
  
  if (cookiesConfigs[lc]) {
    // console.log(`✅ Loaded ${lc} cookies config`);
    return cookiesConfigs[lc];
  }
  
  // console.log('✅ Loaded English cookies config (fallback)');
  return cookiesConfigs.en;
}